/**
 * Whether the Product Browser may keep the previous products response on
 * screen while the next one loads.
 *
 * react-query's `placeholderData` keeps the last page while search or
 * pagination refetch, which is what we want: the grid stays put instead of
 * flashing skeletons. But two changes make the previous rows misleading:
 *
 * - The product-type tab changed, so the rows belong to the previous tab.
 * - The request switched between a server-paginated page and the
 *   client-paginated full store dataset (or back). The client-side sync-status
 *   filter then runs over a stale 25-row page and shows the one or two rows
 *   that happened to be on it as if they were the whole result — a store that
 *   turned "In sync" on saw a single strap for as long as the full catalog
 *   took to load.
 *
 * In both cases dropping the placeholder lets the grid show its skeletons
 * until the real rows arrive.
 */
export type ProductsPlaceholderShape = {
  productType: unknown;
  clientPaginated: boolean;
};

export const canKeepPreviousProductsPage = (
  previous: ProductsPlaceholderShape | undefined,
  current: ProductsPlaceholderShape,
): boolean => {
  if (!previous) return true;
  if (previous.productType !== current.productType) return false;
  if (previous.clientPaginated !== current.clientPaginated) return false;
  return true;
};

/** Index of each shape field in the `['products', ...]` query key. */
export const PRODUCTS_QUERY_KEY_INDEX = {
  productType: 5,
  clientPaginated: 11,
} as const;

export const productsPlaceholderShapeFromKey = (
  queryKey: readonly unknown[],
): ProductsPlaceholderShape => ({
  productType: queryKey[PRODUCTS_QUERY_KEY_INDEX.productType],
  clientPaginated: queryKey[PRODUCTS_QUERY_KEY_INDEX.clientPaginated] === true,
});
